import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { colors as c } from './theme';

// Two fields, both required. The address is the whole point of the form — the
// EmailJS template opens with "You received an email from {{email}}", and
// without it a message arrives with no way to answer it.
const labelTextStyle = {
  display: 'block',
  marginBottom: 8,
  fontSize: 11,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: c.textDim,
};

const fieldStyle = {
  width: '100%',
  padding: '12px 14px',
  background: c.bg,
  border: `1px solid ${c.border}`,
  borderRadius: 2,
  color: c.text,
  fontSize: 14,
  outline: 'none',
  fontFamily: 'inherit',
};

const focusField = e => (e.target.style.borderColor = c.accent);
const blurField = e => (e.target.style.borderColor = c.border);

const ContactModal = ({ onClose }) => {
  const [form, setForm] = useState({ email: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    const email = form.email.trim();
    if (!email) return; // native validation already blocks this
    setStatus('sending');
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          // The address under every name the template and its Reply-To field
          // might be wired to. `subject`/`title` are derived rather than asked
          // for, so a template that puts them in the subject line isn't blank.
          email,
          from_email: email,
          reply_to: email,
          subject: `New message from ${email}`,
          title: `New message from ${email}`,
          message: form.message,
          to_email: 'victor@dzose.com',
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: c.scrim,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: c.card,
          border: `1px solid ${c.border}`,
          borderRadius: 2,
          padding: 40,
          width: '100%',
          maxWidth: 520,
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'none',
            border: 'none',
            color: c.textDim,
            fontSize: 20,
            cursor: 'pointer',
            lineHeight: 1,
          }}
        >
          ×
        </button>

        {status === 'sent' ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p
              style={{
                fontFamily: "'Instrument Serif', 'Times New Roman', serif",
                fontSize: 32,
                color: c.emphasis,
                margin: '0 0 12px',
              }}
            >
              Sent.
            </p>
            <p style={{ color: c.textDim, fontSize: 15 }}>We'll be in touch.</p>
            <button
              onClick={onClose}
              style={{
                marginTop: 28,
                padding: '12px 20px',
                borderRadius: 2,
                border: `1px solid ${c.accent}`,
                background: c.accent,
                color: c.onAccent,
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: 0.4,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p
              style={{
                fontFamily: "'Instrument Serif', 'Times New Roman', serif",
                fontSize: 28,
                margin: '0 0 28px',
                color: c.text,
              }}
            >
              Get in touch
            </p>

            <label style={{ display: 'block', marginBottom: 18 }}>
              <span style={labelTextStyle}>Email</span>
              <input
                type="email"
                required
                autoComplete="email"
                value={form.email}
                onChange={set('email')}
                placeholder="you@example.com"
                style={fieldStyle}
                onFocus={focusField}
                onBlur={blurField}
              />
              <span
                style={{
                  display: 'block',
                  marginTop: 6,
                  fontSize: 12,
                  color: c.textDim,
                }}
              >
                So we can reply to you.
              </span>
            </label>

            <label style={{ display: 'block', marginBottom: 28 }}>
              <span style={labelTextStyle}>Message</span>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={set('message')}
                placeholder="Tell us what you need…"
                style={{ ...fieldStyle, resize: 'vertical' }}
                onFocus={focusField}
                onBlur={blurField}
              />
            </label>

            {status === 'error' && (
              <p style={{ color: c.danger, fontSize: 13, marginBottom: 16 }}>
                Something went wrong — please try emailing victor@dzose.com
                directly.
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'sending'}
              style={{
                width: '100%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '12px 20px',
                borderRadius: 2,
                border: `1px solid ${c.accent}`,
                background: c.accent,
                color: c.onAccent,
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: 0.4,
                cursor: status === 'sending' ? 'default' : 'pointer',
                opacity: status === 'sending' ? 0.6 : 1,
              }}
            >
              {status === 'sending' ? 'Sending…' : 'Send message'}{' '}
              <span>→</span>
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactModal;
